import React, { useRef, useLayoutEffect } from "react";
import StarVector from "../../../StarVector/StarVector";
import * as futureProofStyles from "./futureProof.module.scss";
import futureProofImg from "../../../../assets/images/future-proof.jpg";

import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { StaticImage } from "gatsby-plugin-image";

gsap.registerPlugin(ScrollTrigger);

const FutureProof = () => {
  const futureProofSectionRef = useRef();
  const imgRevealWrapperRef = useRef();

  const titleString =
    "Sincere, future-proof solutions by fullstack experts wrapped in an exclusive service. ";
  const descString =
    "Pure genius, no fluff. Nothing to inflate your bills, waste your time or stall you. We deliver top-notch results, but we only work on problems worth our time. We love solving frustrating problems.";

  const verticalLinesRef = useRef();

  useLayoutEffect(() => {
    const imgRevealWrapperTween = gsap.fromTo(
      imgRevealWrapperRef.current,
      {
        opacity: 0,
        y: "10%",
      },
      {
        duration: 0.5,
        delay: 0.3,
        opacity: 1,
        y: "0%",
        scrollTrigger: {
          id: "Future proof img",
          trigger: futureProofSectionRef.current,
          start: "top center-=200",
          end: "top top",
          markers: false,
        },
      }
    );

    const fadeInUpFutureProofTextAnimation = gsap.fromTo(
      `.FP-tweenWrapper`,
      {
        opacity: 0,
        y: 100,
      },
      {
        opacity: 1,
        y: 0,
        duration: 0.5,
        scrollTrigger: {
          id: "Future proof text",
          trigger: futureProofSectionRef.current,
          start: "top center",
          end: "top top",
          markers: false,
        },
      }
    );

    let proxy = { skew: 0 },
      skewSetter = gsap.quickSetter(".skewElem", "skewX", "deg"),
      clamp = gsap.utils.clamp(-20, 20);

    ScrollTrigger.create({
      onUpdate: (self) => {
        let skew = clamp(self.getVelocity() / -20);

        if (Math.abs(skew) > Math.abs(proxy.skew)) {
          proxy.skew = skew;
          gsap.to(proxy, {
            skew: 0,
            duration: 5,
            ease: "power3",
            overwrite: true,
            onUpdate: () => skewSetter(proxy.skew),
          });
        }
      },
    });

    gsap.set(verticalLinesRef.current, {
      transformOrigin: "right center",
      force3D: true,
    });

    return () => {
      fadeInUpFutureProofTextAnimation.kill();
      imgRevealWrapperTween.kill();
    };
  }, []);

  return (
    <section
      ref={futureProofSectionRef}
      className={`es-section__dark ${futureProofStyles.futureProofSection}`}
    >
      <div className={futureProofStyles.futureProofVectorWrapper}>
        <svg
          width="1434"
          height="252"
          viewBox="0 0 1434 252"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            ref={verticalLinesRef}
            className="skewElem"
            d="M0 1L-1.09278e-05 251M895.625 1.00004L895.625 251M99.5139 1L99.5139 251M995.139 1.00004L995.139 251M199.028 1.00001L199.028 251M1094.65 1.00005L1094.65 251M298.542 1.00001L298.542 251M1194.17 1.00005L1194.17 251M378.153 1.00002L378.153 251M1273.78 1.00006L1273.78 251M497.569 1.00002L497.569 251M1393.19 1.00006L1393.19 251M597.083 1.00003L597.083 251M696.597 1.00003L696.597 251M796.111 1.00003L796.111 251M19.9028 1L19.9028 251M915.528 1.00004L915.528 251M119.417 1.00001L119.417 251M1015.04 1.00004L1015.04 251M218.931 1.00001L218.931 251M1114.56 1.00005L1114.56 251M318.444 1.00001L318.444 251M1214.07 1.00005L1214.07 251M417.958 1.00002L417.958 251M1313.58 1.00006L1313.58 251M517.472 1.00002L517.472 251M1413.1 1.00006L1413.1 251M616.986 1.00003L616.986 251M716.5 1.00003L716.5 251M816.014 1.00004L816.014 251M39.8056 1L39.8055 251M935.431 1.00004L935.431 251M139.319 1.00001L139.319 251M1034.94 1.00005L1034.94 251M238.833 1.00001L238.833 251M1134.46 1.00005L1134.46 251M338.347 1.00001L338.347 251M1233.97 1.00005L1233.97 251M437.861 1.00002L437.861 251M1333.49 1.00006L1333.49 251M537.375 1.00002L537.375 251M1433 1.00006L1433 251M636.889 1.00003L636.889 251M736.403 1.00003L736.403 251M835.917 1.00004L835.917 251M59.7083 1L59.7083 251M955.333 1.00004L955.333 251M159.222 1.00001L159.222 251M1054.85 1.00005L1054.85 251M258.736 1.00001L258.736 251M1154.36 1.00005L1154.36 251M358.25 1.00002L358.25 251M1253.88 1.00005L1253.88 251M457.764 1.00002L457.764 251M1353.39 1.00006L1353.39 251M557.278 1.00002L557.278 251M656.792 1.00003L656.792 251M756.306 1.00003L756.306 251M855.819 1.00004L855.819 251M79.6111 1L79.6111 251M975.236 1.00004L975.236 251M179.125 1.00001L179.125 251M1074.75 1.00005L1074.75 251M278.639 1.00001L278.639 251M1174.26 1.00005L1174.26 251M398.056 1.00002L398.056 251M1293.68 1.00006L1293.68 251M477.667 1.00002L477.667 251M1373.29 1.00006L1373.29 251M577.181 1.00003L577.181 251M676.694 1.00003L676.694 251M776.208 1.00003L776.208 251M875.722 1.00004L875.722 251"
            stroke="url(#paint0_linear_515_2283)"
            strokeMiterlimit="2.61"
          />
          <defs>
            <linearGradient
              id="paint0_linear_515_2283"
              x1="151.521"
              y1="4.56389"
              x2="151.521"
              y2="251.116"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#0066CC" />
              <stop offset="0.513542" stopColor="#EB3300" />
              <stop offset="1" stopColor="white" />
            </linearGradient>
          </defs>
        </svg>
      </div>
      <div className={futureProofStyles.futureProofRow}>
        <div className={futureProofStyles.futureProofCol}>
          <div
            className={`FP-tweenWrapper ${futureProofStyles.futureProofTextWrapper}`}
          >
            <h3>
              <StarVector />
              {titleString}
            </h3>
            <p>
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; Pure
              genius, no fluff. Nothing to inflate your bills, waste your time
              or stall you. We deliver top-notch results, but we only work on
              <strong>
                <span>problems&nbsp;</span>
                <span>worth&nbsp;</span>
                <span>our&nbsp;</span>
                <span>time.&nbsp;</span>
              </strong>
              We love solving frustrating problems.
            </p>
          </div>
        </div>
        <div className={futureProofStyles.futureProofCol}>
          <div
            className={futureProofStyles.futureProofImgWrapper}
            ref={imgRevealWrapperRef}
          >
            <StaticImage
              src="../../../../assets/images/future-proof.jpg"
              alt="Tech rehab"
              layout="fullWidth"
              placeholder="none"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default FutureProof;
